/**
 * B 线卖家精灵镜像配置：调度、即时执行、稳定性矩阵与运行日志。
 * 由选品配置入口装配，保留既有 DOM ID、接口 DTO 和表单文案。
 */
export function createProductResearchMirrorController({
  apiGet,
  apiPost,
  apiPut,
  appendText,
  createElement,
  createEmptyStatus,
  createJobCard,
  createStatus,
  getCheckedValues,
  getElementById,
  pad2,
  replaceChildren,
  setButtonLoading,
  setCheckedValues,
  setFeedback,
  setText,
  showToast,
  confirmFn = globalThis.confirm,
} = {}) {
  let runSiteCodes = null;

  function restoreRunSiteSelection() {
    if (!runSiteCodes) runSiteCodes = ['US'];
    setCheckedValues('pr-b-run-site-checkboxes', runSiteCodes);
  }

  function rememberRunSiteSelection() {
    runSiteCodes = getCheckedValues('pr-b-run-site-checkboxes');
  }

  function renderRuns(runs) {
    const element = getElementById('pr-b-runs-list');
    if (!element) return;
    if (!runs.length) {
      replaceChildren(element, createEmptyStatus('pr-config-status-muted', '暂无 B 线镜像执行记录'));
      return;
    }
    const cards = runs.slice(0, 15).map((run) => createJobCard(run, { mirror: true }));
    replaceChildren(element, ...cards);
    element.querySelectorAll('[data-mirror-cancel]').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.getAttribute('data-mirror-cancel');
        if (!id || !confirmFn(`确认取消镜像任务 #${id}？`)) return;
        try {
          await apiPost(`/api/admin/product-research/mirror/runs/${id}/cancel`);
          showToast('已请求取消', 'success');
          await loadSchedule();
        } catch (error) {
          showToast(error.message || '取消失败', 'error');
        }
      });
    });
  }

  async function loadSchedule() {
    try {
      const data = await apiGet('/api/admin/product-research/mirror/schedule');
      const schedule = data.schedule || {};
      const enabled = getElementById('pr-b-schedule-enabled');
      if (enabled) enabled.checked = Boolean(schedule.enabled);
      const time = getElementById('pr-b-schedule-time');
      if (time) time.value = `${pad2(schedule.hour ?? 3)}:${pad2(schedule.minute ?? 30)}`;
      const interval = getElementById('pr-b-schedule-interval');
      if (interval) interval.value = schedule.intervalDays ?? 2;
      const timezone = getElementById('pr-b-schedule-timezone');
      if (timezone) timezone.value = schedule.timezone || 'Asia/Shanghai';
      const runMode = getElementById('pr-b-schedule-run-mode');
      if (runMode) runMode.value = schedule.runMode || 'full_backfill';
      if (Array.isArray(schedule.siteCodes) && schedule.siteCodes.length) {
        setCheckedValues('pr-b-schedule-site-checkboxes', schedule.siteCodes);
      }
      restoreRunSiteSelection();
      const status = getElementById('pr-b-schedule-status');
      if (status) {
        replaceChildren(status, createStatus(schedule.enabled ? 'pr-config-status-ok' : 'pr-config-status-muted', schedule.enabled ? '已启用' : '未启用'));
        appendText(status, ` · ${schedule.registered ? '任务已注册' : '任务未注册'}`);
        if (schedule.running) {
          appendText(status, ' · ');
          status.append(createStatus('pr-config-status-info', '定时轮次执行中'));
        }
        appendText(status, ` · 每 ${schedule.intervalDays ?? 2} 天 ${pad2(schedule.hour)}:${pad2(schedule.minute)}（${schedule.timezone || 'Asia/Shanghai'}） · 模式 ${schedule.runMode || '-'} · 站点 ${(schedule.siteCodes || []).join(', ') || '-'}`);
      }
      renderRuns(data.latestRuns || []);
    } catch (error) {
      setText('pr-b-schedule-status', `加载失败: ${error.message}`);
      setText('pr-b-runs-list', `加载失败: ${error.message}`);
    }
  }

  async function saveSchedule() {
    const button = getElementById('pr-b-schedule-save');
    if (button) button.disabled = true;
    try {
      const siteCodes = getCheckedValues('pr-b-schedule-site-checkboxes');
      if (!siteCodes.length) throw new Error('请至少选择一个可调度站点');
      const time = getElementById('pr-b-schedule-time')?.value || '03:30';
      await apiPut('/api/admin/product-research/mirror/schedule', {
        enabled: Boolean(getElementById('pr-b-schedule-enabled')?.checked),
        hour: Number(time.split(':')[0]),
        minute: Number(time.split(':')[1]),
        intervalDays: Number(getElementById('pr-b-schedule-interval')?.value || 2),
        timezone: getElementById('pr-b-schedule-timezone')?.value || 'Asia/Shanghai',
        run_mode: getElementById('pr-b-schedule-run-mode')?.value || 'full_backfill',
        site_codes: siteCodes,
      });
      showToast('B 线调度已保存并应用', 'success');
      await loadSchedule();
    } catch (error) {
      showToast(error.message || '保存失败', 'error');
    } finally {
      if (button) button.disabled = false;
    }
  }

  async function runNow() {
    const siteCodes = getCheckedValues('pr-b-run-site-checkboxes');
    if (!siteCodes.length) {
      setFeedback('pr-b-run-feedback', '请至少选择一个站点', 'error');
      return;
    }
    runSiteCodes = [...siteCodes];
    const body = {
      site_codes: siteCodes,
      run_mode: getElementById('pr-b-run-mode')?.value || 'probe',
      notes: 'admin-manual',
    };
    const maxPages = Number(getElementById('pr-b-run-max-pages')?.value);
    if (Number.isFinite(maxPages) && maxPages > 0) body.max_pages = maxPages;
    setButtonLoading('pr-b-run-now', true);
    setFeedback('pr-b-run-feedback', `正在提交 ${siteCodes.join('、')} …`, 'info');
    try {
      const data = await apiPost('/api/admin/product-research/mirror/runs/batch', body);
      setFeedback('pr-b-run-feedback', `已提交 ${siteCodes.length} 个站点的串行任务（批次 ${data?.id ?? '-'}）`, 'success');
      showToast('B 线镜像串行任务已提交', 'success');
      await loadSchedule();
    } catch (error) {
      setFeedback('pr-b-run-feedback', `提交失败: ${error.message}`, 'error');
    } finally {
      setButtonLoading('pr-b-run-now', false);
    }
  }

  function renderMatrixReport(report) {
    const element = getElementById('pr-b-matrix-report');
    if (!element) return;
    if (!report) {
      replaceChildren(element, createEmptyStatus('pr-config-status-muted', '尚无矩阵结果'));
      return;
    }
    const issues = Array.isArray(report.issues) ? report.issues : [];
    const summary = createElement('div');
    summary.append(createElement('strong', report.passed ? 'pr-config-status-ok' : 'pr-config-status-error', report.passed ? 'PASS' : 'FAIL'));
    appendText(summary, ` · 完成率 ${report.completion_rate_pct ?? '-'}% · 成功分片 ${report.success_shards ?? 0}/${report.planned_shards ?? 0} · 入库 ${report.total_items ?? 0} · 预设 ${report.preset || '-'} · 站点 ${(report.sites || []).join(', ')}`);
    const meta = createElement('div', 'pr-config-muted pr-config-matrix-meta', `${report.started_at || ''} → ${report.finished_at || ''}${issues.length ? ` · 问题 ${issues.length} 条` : ' · 无问题清单'}`);
    const nodes = [summary, meta];
    if (issues.length) {
      const details = createElement('details', 'pr-config-matrix-issues');
      details.append(createElement('summary', '', '问题清单'));
      const list = createElement('ul');
      list.append(...issues.slice(0, 40).map((issue) => createElement('li', '', `${issue?.shard_key || issue?.type || '-'} · ${issue?.error_message || issue?.message || issue?.status || ''}`)));
      details.append(list);
      nodes.push(details);
    }
    replaceChildren(element, ...nodes);
  }

  async function loadMatrix() {
    const status = getElementById('pr-b-matrix-status');
    try {
      const data = await apiGet('/api/admin/product-research/mirror/matrix');
      if (status) {
        replaceChildren(status, createStatus(data.running ? 'pr-config-status-info' : 'pr-config-status-muted', data.running ? '矩阵执行中…' : '矩阵空闲'));
        if (data.running) appendText(status, '（可稍后点刷新）');
      }
      renderMatrixReport(data.lastResult || null);
    } catch (error) {
      if (status) status.textContent = `加载失败: ${error.message}`;
    }
  }

  async function runMatrix(preset) {
    const label = preset === 'full' ? '全量生产矩阵' : '半量稳定性矩阵';
    if (!confirmFn(`确认启动${label}？将按 7 价格层容错串行执行，可能持续较长时间。`)) return;
    const buttonId = preset === 'full' ? 'pr-b-matrix-full' : 'pr-b-matrix-half';
    setButtonLoading(buttonId, true);
    setFeedback('pr-b-matrix-feedback', `正在受理${label}…`, 'info');
    try {
      const data = await apiPost('/api/admin/product-research/mirror/matrix', { preset, notes: `admin-${preset}` });
      const plan = data.plan || {};
      setFeedback('pr-b-matrix-feedback', `已受理：${(plan.sites || []).join(', ')} · ${plan.planned_shards || '-'} 分片 · 计划约 ${plan.planned_items || '-'} 条`, 'success');
      showToast(`${label}已启动`, 'success');
      await loadMatrix();
      await loadSchedule();
    } catch (error) {
      setFeedback('pr-b-matrix-feedback', `启动失败: ${error.message}`, 'error');
    } finally {
      setButtonLoading(buttonId, false);
    }
  }

  return Object.freeze({ loadSchedule, saveSchedule, runNow, loadMatrix, runMatrix, rememberRunSiteSelection, restoreRunSiteSelection });
}
